/**
 * The 30 weapon classes, one representative weapon each.
 *
 * Built as a function rather than a module-level constant because every
 * `terrain.emit` and `emits` field needs real material ids, and those only exist
 * after `registerMaterials()`. Calling this at import time would silently bake
 * in zeros.
 *
 * Timings are authored in real milliseconds. There used to be a global
 * `SWING_SPEED = 2.5` divisor in the wielder because these triples had been
 * authored slow; that made the numbers here fiction, and three downstream
 * consumers — including the swing-animation length in game.ts — read the raw
 * values and got a figure 2.5× too long. The triples are now written at the
 * speed they run at, and the divisor is gone.
 *
 * Two rules are enforced structurally rather than by convention:
 *   - `weight` is the ONLY handling knob authored per weapon. Hitstop, drag,
 *     recovery, camera kick and knockback all fall out of it via the helpers
 *     below, so a heavy weapon is heavy in six correlated ways.
 *   - Every weapon gets the same combo skeleton from `buildCombo` — three
 *     lights, a heavy finisher, a dash node, an air node and a pogo down-air.
 *     Weapons differ by overriding the skeleton, never by omitting parts of it,
 *     which is what makes the universal rules of docs/04 §4 true by
 *     construction instead of true by review.
 */

import { lerp } from '../core/math';
import type { Vec2 } from '../core/math';
import { DamageType } from '../entity/ecs';
import type { DamageTypeId } from '../entity/ecs';
import { MAT } from '../sim/materialData';
import { matId } from '../sim/materials';
import type { MatId } from '../sim/materials';
import { parseShapeMask, WEAPON_NODE_KEYS } from '../sim/shapeMask';
import { weaponSpecFor, type WeaponSpec } from './weaponMask';
import {
  PrimerKind, WeaponTag, weaponTags,
} from './types';
import type {
  ComboNode, FireSpec, HitboxShape, PrimerKindId, Rarity, ResolvedWeapon, SlotKind,
  SlotSpec, StatKey, TerrainInteraction, WeaponClass, WeaponDef, WeaponTagMask,
} from './types';

// ------------------------------------------------------- authored documents
//
// One `.pxm` per weapon that has migrated off its inline literal — see
// `weaponMask.ts`'s own header and `docs/29-pxm-format.md` §4.5 for the
// format. A weapon without a document (29 of 30, today) is unaffected: the
// lookup below returns `undefined` and every call site keeps reading the
// `Spec` literal exactly as it always has.

const WEAPON_TEXT: Record<string, string> = import.meta.glob('./weapons/*.pxm', {
  query: '?raw',
  import: 'default',
  eager: true,
});

/**
 * The authored weapon documents the build holds, as `[id, repo-relative
 * path]` — `BODY_PLAN_FILES`'s own shape, one registry over, for the same
 * consumer: a tool listing what can be opened, from the registry that loads
 * it rather than a directory scan. Derived from the glob's own keys rather
 * than a full parse: unlike `getWeaponDocs()`, nothing here resolves a
 * material, so there is no `registerMaterials()` ordering to protect and no
 * reason for this to be lazy.
 */
export const WEAPON_FILES: readonly (readonly [string, string])[] = Object.keys(WEAPON_TEXT)
  .map((key) => key.replace(/^\.\//, 'src/combat/'))
  .sort()
  .map((path) => [(path.split('/').pop() as string).replace(/\.pxm$/, ''), path] as const);

/**
 * A weapon's source text by its repo-relative path — `planTextByPath`'s own
 * shape, one registry over, for the same consumer: the in-game editor has no
 * filesystem to read the document from. The glob key keeps `weapons/` (it is
 * `import.meta.glob('./weapons/*.pxm', ...)`'s own relative path), so only
 * the `src/combat/` prefix strips.
 */
export function weaponTextByPath(rel: string): string | undefined {
  return WEAPON_TEXT[rel.replace(/^src\/combat\//, './')];
}

/**
 * Every authored weapon `.pxm`, resolved once per `buildWeapons()` call —
 * not at module load, for `buildWeapons` itself's own reason (see the
 * header of this file): a blade's material name resolves through `matId`,
 * and that only knows real ids after `registerMaterials()` has run. A
 * module-level constant here would resolve `STEEL` against an empty table
 * and throw on every import, `buildWeapons()` calls or not.
 */
let weaponDocs: ReadonlyMap<string, WeaponSpec> | null = null;

function getWeaponDocs(): ReadonlyMap<string, WeaponSpec> {
  if (weaponDocs === null) {
    const out = new Map<string, WeaponSpec>();
    for (const [path, text] of Object.entries(WEAPON_TEXT)) {
      const doc = parseShapeMask(text, path);
      out.set(doc.name, weaponSpecFor(doc, matId));
    }
    weaponDocs = out;
  }
  return weaponDocs;
}

/**
 * Overlay a document's `weight` and per-node timing onto a `Spec`, before
 * `make()` runs — `weight` feeds `recoveryScale`/`knockbackScale` inside
 * `make()`, so it has to land before that call, not after.
 *
 * Node overrides are merged onto whatever combat-balance override the
 * literal already carries for that key (`dmg`, `primer`, `hit`, ...) rather
 * than replacing it — a document only ever adds timing, never drops a
 * combat-balance decision that stayed in code on purpose.
 */
function withDoc(s: Spec): Spec {
  const doc = getWeaponDocs().get(s.id);
  if (doc === undefined) return s;
  const combo = { ...s.combo };
  for (const [key, o] of doc.nodes) {
    combo[key] = {
      ...combo[key],
      ...(o.w !== undefined && { w: o.w }),
      ...(o.a !== undefined && { a: o.a }),
      ...(o.r !== undefined && { r: o.r }),
      ...(o.wm !== undefined && { wm: o.wm }),
      ...(o.am !== undefined && { am: o.am }),
      ...(o.rm !== undefined && { rm: o.rm }),
    };
  }
  return { ...s, weight: doc.weight, combo };
}

/**
 * Attach a document's blade and per-node arc to an already-built
 * `WeaponDef` — the two fields `make()`/`buildCombo` have no slot for, so
 * they land as a second pass rather than threading through both.
 *
 * `WEAPON_NODE_KEYS` and `SKELETON` share one order (see `weaponMask.ts`'s
 * own comment on why the key list lives in `shapeMask.ts` rather than being
 * derived from `SKELETON`, which is private to this file), so zipping
 * `def.combo` against it by index is exact — asserted by
 * `tests/weaponMask.test.ts`'s "the node keys stay in the order buildCombo
 * builds them" test, which is what a reordering of either list would fail.
 */
function attachDoc(def: WeaponDef): WeaponDef {
  const doc = getWeaponDocs().get(def.id);
  if (doc === undefined) return def;
  const combo = def.combo.map((node, i) => {
    const key = WEAPON_NODE_KEYS[i];
    const arc = doc.nodes.get(key)?.arc;
    return arc === undefined ? node : { ...node, arc };
  });
  return { ...def, blade: doc.blade, combo };
}

// ------------------------------------------------------- weight → feel

/**
 * The freeze a landed blow puts on **the body that took it**, in ms.
 *
 * docs/02 §4's 40–90-by-weight number, and the impact read the doc argues for:
 * a heavy weapon should visibly stop the thing it hit for longer than a light
 * one does. That claim is about the target, so this is now the target's number
 * alone — see `attackerHitstopMs` for the other half, which used to be this
 * same value and should never have been.
 */
export function targetHitstopMs(weight: number): number {
  return lerp(40, 90, clamp01(weight));
}

/**
 * Fraction of the node that landed a hit that the *attacker* spends frozen.
 *
 * ### Why this is a fraction and not a constant
 *
 * The attacker's freeze is not a feel knob, it is a throughput tax: the wielder
 * returns early while `w.hitstop[i] > 0` (wielder.ts), so those milliseconds
 * are milliseconds the hand cannot swing in. Phase 0 measured the tax across
 * the 16 discrete melee classes at **23.0% (charger_lance) to 42.5%
 * (flense_dagger)** — a 19.5 percentage-point spread in effective DPS that no
 * band in any doc mentions.
 *
 * The obvious fix, and the one #37 recommended, is to make the attacker's
 * freeze a constant so it stops scaling with weight. **Measured, that makes it
 * worse.** The spread does not come from the freeze varying (46–86 ms, 1.9×);
 * it comes from the *cycle* varying (187–852 ms for a light chain, 4.6×). A
 * flat freeze is therefore a much larger share of a dagger's chain than of a
 * hammer's:
 *
 *     freeze              spread of the throughput tax
 *     hitstopMs(weight)   19.5 pp   (23.0% .. 42.5%)   ← before
 *     constant 20 ms      17.7 pp   ( 6.6% .. 24.3%)
 *     constant 30 ms      22.9 pp   ( 9.6% .. 32.5%)
 *     constant 40 ms      26.7 pp   (12.3% .. 39.1%)
 *     constant 65 ms      32.4 pp   (18.6% .. 51.0%)
 *     0.45 × the node      0.0 pp   (31.0% for every weapon)   ← now
 *
 * Tying the freeze to the swing that earned it is the only form that makes the
 * tax equal, because the tax is `f·M / (M + f·M)` = `f / (1 + f)` — the cycle
 * length `M` cancels. It also keeps the attacker-side weight feel that a flat
 * constant would have thrown away, since a heavy weapon's nodes are longer.
 *
 * ### Why 0.45
 *
 * Derived, not chosen: `f / (1 + f)` = 31.0% is the mean tax the catalogue
 * already pays (30.99% across the 16 discrete melee classes), so collapsing the
 * spread costs the catalogue nothing on average. No authored weapon value moves.
 * Phase 7 owns any change to this number.
 */
export const ATTACKER_HITSTOP_FRAC = 0.45;

/**
 * Horizontal velocity multiplier applied while an attack runs. Never 0 —
 * attacks drag, they do not stop you (docs/02 §4).
 */
export function attackDrag(weight: number): number {
  return lerp(0.9, 0.6, clamp01(weight));
}

/** Camera kick amplitude in units. */
export function cameraKick(weight: number): number {
  return 1.2 + clamp01(weight) * 6.8;
}

/** Recovery stretch: heavy weapons commit longer for the same authored value. */
export function recoveryScale(weight: number): number {
  return lerp(0.85, 1.45, clamp01(weight));
}

/** Knockback scale. Heavier weapons throw further. */
export function knockbackScale(weight: number): number {
  return lerp(0.6, 2.0, clamp01(weight));
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

// --------------------------------------------------------- node timing
//
// This lives here rather than in `wielder.ts` for one reason: `resolve.ts` has
// to know how long the node that landed a hit takes, and `wielder.ts` already
// imports `resolve.ts`. Putting the arithmetic in the lowest layer both of them
// already depend on is what stops it being copied into the second one — which is
// the failure mode `tests/balance.test.ts` was built to catch, and which it was
// itself committing until this change.

/**
 * The only thing `WeaponDef.cadenceMulBase` is for.
 *
 * `resolved.cadence` is `cadenceMulBase × rolled.cadenceMul × every module
 * cadenceMul`, so this is `1 / Πmul` and nothing else: the authored magnitude
 * cancels exactly. A weapon carrying a module that raises cadence gets shorter
 * windup, active and recovery in proportion; a bare weapon runs its node triple
 * as authored, in real milliseconds.
 *
 * The field used to be called `cadence` and this ratio is why that name had to
 * go — see `WeaponDef.cadenceMulBase`, and #39 for the three candidate swing
 * rates none of which this number is.
 */
export function cadenceScale(wp: ResolvedWeapon): number {
  if (wp.def.cadenceMulBase <= 0) return 1;
  return wp.def.cadenceMulBase / Math.max(0.05, wp.cadence);
}

export interface NodeTiming {
  windup: number;
  active: number;
  recovery: number;
  /** ms of one full node, windup through recovery. */
  total: number;
}

/**
 * What one node costs in real milliseconds — the numbers the hand actually
 * counts down, not the authored triple.
 *
 * `rhythmRecovery` is `rhythmRecoveryMul(state)` (wielder.ts), 1 at rhythm 0.
 * Trigger-window `recoveryMul`s are deliberately *not* folded in: they are a
 * conditional payload, they live in `nodeRecovery`, and a hitstop that changed
 * length depending on whether an `onKill` window happened to be armed would be
 * unmeasurable.
 */
export function nodeTiming(
  wp: ResolvedWeapon,
  node: ComboNode,
  rhythmRecovery = 1,
): NodeTiming {
  const cs = cadenceScale(wp);
  const windup = Math.max(1, node.windup * wp.windupMul * cs);
  const active = Math.max(1, node.active * cs);
  const recovery = Math.max(1, node.recovery * wp.recoveryMul * rhythmRecovery * cs);
  return { windup, active, recovery, total: windup + active + recovery };
}

/**
 * The freeze a landed blow puts on **the attacker**, in ms.
 *
 * A fixed share of the node that earned it — see `ATTACKER_HITSTOP_FRAC` for
 * why that shape and why 0.45. Measured at rhythm 0, so a player riding the
 * rhythm meter (which shortens recovery, not the freeze) pays a slightly larger
 * share; that is the one residual and it is disclosed rather than hidden.
 */
export function attackerHitstopMs(wp: ResolvedWeapon, node: ComboNode | null): number {
  // A contact with no node behind it — a projectile that outlived its node
  // reference — falls back to the target's number, which is what it used to be
  // for everyone.
  if (!node) return targetHitstopMs(wp.weight);
  return ATTACKER_HITSTOP_FRAC * nodeTiming(wp, node).total;
}

// ------------------------------------------------------- resources (§11)

/**
 * What `WeaponInstance.charge` means for a class.
 *
 * One mutable per-run scalar, five readings, because the identities docs/04 §3
 * authors — the drill that "consumes fuel", the living weapon that "has
 * hunger", the chain weapon's "spin-up state", the shield that "blocks" — are
 * the same shape of number pointed in different directions. `battery` is the
 * default and is what `ModuleEffect.melee.chargePerHit` fills: galvanic's
 * "charge builds on hits, discharge on heavy" (§5.2).
 *
 * `guard` is the shield's, and it is the one that reads *down* from full:
 * blocking spends it, and a shield with none left is a shield that breaks.
 * Without it the shield was a strictly worse dagger — same code path, lower
 * damage, no backstab — which is exactly what §9 forbids.
 */
export type ChargeKind = 'fuel' | 'hunger' | 'spin' | 'battery' | 'guard';

export function chargeKindOf(def: WeaponDef): ChargeKind {
  switch (def.class) {
    case 'drill': return 'fuel';
    case 'living': return 'hunger';
    case 'chain': return 'spin';
    case 'shield': return 'guard';
    default: return 'battery';
  }
}

/**
 * Value a freshly folded instance starts at. A tank starts full and so does a
 * shield's guard; nothing else does.
 */
export function initialCharge(def: WeaponDef): number {
  const k = chargeKindOf(def);
  return k === 'fuel' || k === 'guard' ? 1 : 0;
}

export function magazineOf(def: WeaponDef): number {
  return def.magazine ?? 0;
}

/**
 * What one trigger pull of `node` costs, in rounds.
 *
 * Derived from the burst rather than authored, so the launcher's three-shell
 * heavy costs three and the pistol's double-tap costs two without anyone having
 * to keep a second table in sync with the first. Measured against the weapon's
 * *authored* opener, so a `countAdd` module adds pellets without also adding a
 * bill — a module that costs more to fire than the thing it is bolted to is the
 * §9 anti-pattern pointed backwards.
 */
export function ammoCostOf(def: WeaponDef, node: ComboNode): number {
  if (!def.magazine || !node.fire) return 0;
  const base = def.combo.find((n) => n.entry === 'light')?.fire?.count ?? 1;
  if (base <= 0) return 1;
  return Math.max(1, Math.round(node.fire.count / base));
}

/**
 * Re-contact interval for continuous weapons, ms — and the interval a
 * continuous weapon spends one round of its magazine on.
 *
 * These are one number, and until now they were two: `CONTINUOUS_HIT_MS` lived
 * unexported in `wielder.ts` and this constant lived here declaring itself
 * "matched to" it, with nothing anywhere asserting the match. Either could have
 * been edited alone and a beam would have silently started billing rounds at a
 * rate other than the one it deals damage at. There is now one declaration and
 * `wielder.ts` imports it.
 *
 * Why the interval exists at all: a drill, beam or sprayer skips per-node hit
 * dedup by design — sustained contact has to keep dealing damage. Without an
 * interval it dealt damage every substep, which at 120 Hz is 20 ticks inside one
 * active window. Gating on wall time makes both the damage rate and the ammo
 * burn frame-rate independent, which is the actual requirement.
 */
export const CONTINUOUS_HIT_MS = 100;

export function isContinuous(def: WeaponDef): boolean {
  return (def.tags & WeaponTag.CONTINUOUS) !== 0;
}

/**
 * How often this weapon lands a hit, in hertz. The number to show a player.
 *
 * One concept, computed for whichever mechanism the weapon uses. A discrete
 * weapon lands one hit per combo node, so its rate is the **opener** — the rate
 * from clean, spaced attacks, which #39 ruled canonical out of the three that
 * existed (authored / opener / chained) and which every band in docs/15 is
 * quoted against. A continuous weapon does not swing at all: a held stream
 * re-contacts every `CONTINUOUS_HIT_MS`, and its node timing describes how long
 * the trigger takes to come up, not how often it hurts anything.
 *
 * The two mechanisms are not an exception to the ruling, they are the ruling
 * applied honestly. Quoting a kiln beam's node rate would have printed 3.29/s
 * for a weapon that ticks at 10/s — trading the old wrong number for a new one.
 * What went wrong with `cadence` was one *field* meaning two things; this is one
 * *meaning* with two correct derivations, which is the opposite.
 *
 * It lives here, beside both `nodeTiming` and `CONTINUOUS_HIT_MS`, because the
 * alternative was three call sites each composing the arithmetic themselves —
 * and a transcription of exactly this formula is what let `combat.test.ts:1189`
 * look green for months while guarding a claim that was false. Three further
 * copies of the same class were found in Phases 1–2. There is one definition.
 */
export function hitRateHz(wp: ResolvedWeapon): number {
  if (isContinuous(wp.def)) return 1000 / CONTINUOUS_HIT_MS;
  return 1000 / nodeTiming(wp, wp.entries.light).total;
}

// --------------------------------------------------------------- helpers

function v(x: number, y: number): Vec2 {
  return { x, y };
}

function slots(kinetic: number, payload: number, trigger: number): SlotSpec[] {
  return [
    { kind: 'core' as SlotKind, count: 1 },
    { kind: 'kinetic' as SlotKind, count: kinetic },
    { kind: 'payload' as SlotKind, count: payload },
    { kind: 'trigger' as SlotKind, count: trigger },
    { kind: 'frame' as SlotKind, count: 1 },
  ];
}

interface TerrExtra {
  emit?: { mat: MatId; count: number; velocity: number };
  heat?: number;
  charge?: number;
  corrode?: number;
  displaces?: boolean;
}

function terr(
  maxHardness: number,
  radius: number,
  chance: number,
  extra: TerrExtra = {},
): TerrainInteraction {
  return {
    destroy: { maxHardness, radius, chance },
    emit: extra.emit,
    heat: extra.heat,
    charge: extra.charge,
    corrode: extra.corrode,
    displaces: extra.displaces ?? false,
  };
}

function hitbox(p: Partial<HitboxShape>): HitboxShape {
  return {
    kind: p.kind ?? 'arc',
    ox: p.ox ?? 0,
    oy: p.oy ?? 0,
    radius: p.radius ?? 16,
    thickness: p.thickness ?? 8,
    halfAngle: p.halfAngle ?? 1.0,
    angle: p.angle ?? 0,
    throughWalls: p.throughWalls ?? false,
  };
}

function fire(p: Partial<FireSpec>): FireSpec {
  return {
    count: p.count ?? 1,
    spread: p.spread ?? 0,
    speed: p.speed ?? 420,
    lifetime: p.lifetime ?? 1.2,
    pierce: p.pierce ?? 0,
    gravityScale: p.gravityScale ?? 0,
    recoil: p.recoil ?? 0,
    trailMat: p.trailMat,
  };
}

// ------------------------------------------------------------ combo build

/** Per-node overrides. Every field is optional; the skeleton fills the rest. */
interface NodeOpt {
  /** Absolute ms overrides. */
  w?: number;
  a?: number;
  r?: number;
  /** Multipliers against the skeleton's derived timing. */
  wm?: number;
  am?: number;
  rm?: number;
  dmg?: number;
  poise?: number;
  mx?: number;
  my?: number;
  hit?: Partial<HitboxShape>;
  primer?: PrimerKindId;
  grav?: number;
  fire?: Partial<FireSpec>;
}

interface ComboOpt {
  light1?: NodeOpt;
  light2?: NodeOpt;
  light3?: NodeOpt;
  heavy?: NodeOpt;
  dash?: NodeOpt;
  air?: NodeOpt;
  dair?: NodeOpt;
  /** Default hitbox shared by all nodes before per-node overrides. */
  base?: Partial<HitboxShape>;
  /** Default fire spec for projectile weapons. */
  baseFire?: Partial<FireSpec>;
}

const SKELETON: Array<{
  key: keyof Omit<ComboOpt, 'base' | 'baseFire'>;
  suffix: string;
  wm: number;
  am: number;
  rm: number;
  dmg: number;
  poise: number;
  mx: number;
  my: number;
  entry?: 'light' | 'heavy' | 'dash' | 'air';
  pogo?: boolean;
  grav?: number;
}> = [
  { key: 'light1', suffix: 'l1', wm: 1.0, am: 1.0, rm: 0.85, dmg: 0.9, poise: 0.8, mx: 34, my: 0, entry: 'light' },
  { key: 'light2', suffix: 'l2', wm: 0.85, am: 1.0, rm: 0.9, dmg: 1.0, poise: 0.9, mx: 30, my: 0 },
  { key: 'light3', suffix: 'l3', wm: 1.1, am: 1.15, rm: 1.25, dmg: 1.35, poise: 1.3, mx: 52, my: 0 },
  { key: 'heavy', suffix: 'hv', wm: 1.9, am: 1.3, rm: 1.7, dmg: 2.1, poise: 2.2, mx: 46, my: 0, entry: 'heavy' },
  { key: 'dash', suffix: 'da', wm: 0.55, am: 1.1, rm: 0.8, dmg: 1.25, poise: 1.1, mx: 150, my: 0, entry: 'dash' },
  { key: 'air', suffix: 'ar', wm: 0.8, am: 1.0, rm: 0.75, dmg: 1.0, poise: 0.9, mx: 26, my: -18, entry: 'air', grav: 0.55 },
  { key: 'dair', suffix: 'dr', wm: 0.9, am: 1.2, rm: 1.0, dmg: 1.3, poise: 1.4, mx: 0, my: 190, entry: 'air', pogo: true, grav: 1.7 },
];

/**
 * Wiring is fixed: lights chain and loop, everything falls back into the chain,
 * and every node can be dash-cancelled or converted into the heavy finisher.
 * Encoding it once is the only way "universal rules" stays universal after the
 * 29th weapon.
 */
function buildCombo(
  id: string,
  base: { windup: number; active: number; recovery: number },
  cancelAfter: number,
  opt: ComboOpt = {},
): ComboNode[] {
  const n = (s: string) => `${id}:${s}`;
  const out: ComboNode[] = [];

  for (const sk of SKELETON) {
    const o: NodeOpt = opt[sk.key] ?? {};
    const windup = o.w ?? Math.round(base.windup * sk.wm * (o.wm ?? 1));
    const active = o.a ?? Math.round(base.active * sk.am * (o.am ?? 1));
    const recovery = o.r ?? Math.round(base.recovery * sk.rm * (o.rm ?? 1));
    // `cancelWindow` is a fraction **of recovery**, matching `cancelAfter`'s
    // own documented meaning and matching what the state machine compares it
    // against (`h.timer / recovery`, where the timer restarts at recovery).
    //
    // It used to be authored as a fraction of the node's *total* duration and
    // consumed as a fraction of recovery alone, so every weapon was markedly
    // less cancellable than authored: the Ashen sword's 0.6 behaved as 0.80,
    // the Deepcut Auger's 0.40 as 0.86, the Kiln Emitter's 0.45 as 0.88. With
    // the 120 ms input buffer on top, a dash pressed the instant a greatblade
    // swing connected expired before the window it was aimed at ever opened.

    const next =
      sk.key === 'light1'
        ? { onLight: n('l2'), onHeavy: n('hv'), onDash: n('da'), onAir: n('ar') }
        : sk.key === 'light2'
          ? { onLight: n('l3'), onHeavy: n('hv'), onDash: n('da'), onAir: n('ar') }
          : sk.key === 'light3'
            ? { onLight: n('l1'), onHeavy: n('hv'), onDash: n('da'), onAir: n('ar') }
            : sk.key === 'air'
              ? { onLight: n('l1'), onHeavy: n('hv'), onDash: n('da'), onAir: n('dr') }
              : sk.key === 'dair'
                ? { onLight: n('l1'), onHeavy: n('hv'), onDash: n('da') }
                : { onLight: n('l1'), onHeavy: n('hv'), onDash: n('da'), onAir: n('ar') };

    const node: ComboNode = {
      id: n(sk.suffix),
      windup,
      active,
      recovery,
      damageMul: o.dmg ?? sk.dmg,
      poiseMul: o.poise ?? sk.poise,
      movement: v(o.mx ?? sk.mx, o.my ?? sk.my),
      hitbox: hitbox({ ...opt.base, ...o.hit }),
      next,
      cancelWindow: [cancelAfter, 1],
    };

    if (sk.entry) node.entry = sk.entry;
    if (sk.pogo) node.pogo = true;
    if (o.primer !== undefined) node.primerApplied = o.primer;
    const g = o.grav ?? sk.grav;
    if (g !== undefined) node.gravityScale = g;
    if (opt.baseFire || o.fire) node.fire = fire({ ...opt.baseFire, ...o.fire });

    out.push(node);
  }
  return out;
}

// ---------------------------------------------------------- weapon spec

interface Spec {
  id: string;
  cls: WeaponClass;
  name: string;
  rarity?: Rarity;
  /** Optional — a weapon with a `.pxm` document takes its weight from there instead. */
  weight?: number;
  reach: number;
  cadenceMulBase: number;
  windup: number;
  active: number;
  recovery: number;
  cancelAfter?: number;
  move?: Vec2;
  gravityScale?: number;
  damage: number;
  type?: DamageTypeId;
  poise: number;
  knockback?: Vec2;
  primes?: PrimerKindId[];
  detonates?: PrimerKindId[];
  terrain: TerrainInteraction;
  emits?: { mat: MatId; count: number; spread: number };
  slots: SlotSpec[];
  scaling: Partial<Record<StatKey, number>>;
  tags: WeaponTagMask;
  offhandFirst?: boolean;
  desc: string;
  combo?: ComboOpt;
  chargeStates?: WeaponDef['chargeStates'];
  parry?: WeaponDef['parry'];
  magazine?: number;
  reloadMs?: number;
  heatPerShot?: number;
  heatPerSecond?: number;
}

function make(archetype: 'melee' | 'projectile', spec: Spec): WeaponDef {
  const s = withDoc(spec);
  if (s.weight === undefined) {
    throw new Error(`${s.id}: no weight — a weapon's weight is authored in code or in a .pxm document, never neither`);
  }
  const weight = s.weight;
  const cancelAfter = s.cancelAfter ?? 0.6;
  // Recovery is stretched by weight here, once, so every node inherits it.
  const recovery = Math.round(s.recovery * recoveryScale(weight));
  const kb = s.knockback ?? v(90, -20);
  const kbs = knockbackScale(weight);

  return attachDoc({
    id: s.id,
    class: s.cls,
    archetype,
    name: s.name,
    rarity: s.rarity ?? 'common',
    weight,
    reach: s.reach,
    cadenceMulBase: s.cadenceMulBase,
    windup: s.windup,
    active: s.active,
    recovery,
    cancelAfter,
    movementVector: s.move ?? v(38, 0),
    gravityScale: s.gravityScale ?? 1,
    damage: s.damage,
    damageType: s.type ?? DamageType.PHYSICAL,
    poise: s.poise,
    knockback: v(kb.x * kbs, kb.y * kbs),
    combo: buildCombo(
      s.id,
      { windup: s.windup, active: s.active, recovery },
      cancelAfter,
      s.combo,
    ),
    chargeStates: s.chargeStates,
    parry: s.parry,
    magazine: s.magazine,
    reloadMs: s.reloadMs,
    heatPerShot: s.heatPerShot,
    heatPerSecond: s.heatPerSecond,
    primes: s.primes ?? [],
    detonates: s.detonates ?? [],
    terrain: s.terrain,
    emits: s.emits,
    slots: s.slots,
    scaling: s.scaling,
    tags: s.tags,
    offhandFirst: s.offhandFirst,
    desc: s.desc,
  });
}

// ================================================================= weapons

export function buildWeapons(): WeaponDef[] {
  const P = PrimerKind;
  const D = DamageType;
  const out: WeaponDef[] = [];

  // ------------------------------------------------------------ melee (18)

  out.push(make('melee', {
    id: 'ashen_sword', cls: 'sword', name: 'Ashen Longsword',
    // weight: authored in src/combat/weapons/ashen_sword.pxm (issue #168).
    reach: 20, cadenceMulBase: 2.1,
    windup: 36, active: 28, recovery: 68,
    damage: 22, poise: 1, type: D.PHYSICAL,
    primes: [P.STAGGERED], detonates: [P.BLEEDING],
    terrain: terr(3, 5, 0.5),
    slots: slots(1, 2, 1), scaling: { might: 0.7, finesse: 0.4 },
    tags: weaponTags('PARRY', 'DETONATOR'),
    parry: { windowMs: 130, recoveryMs: 190, counterNode: 'ashen_sword:hv', reflects: true, poiseGain: 1.2, primerApplied: P.STAGGERED },
    desc: 'The baseline against which every other class is measured.',
    combo: {
      base: { kind: 'arc', radius: 20, thickness: 9, halfAngle: 1.1 },
      light3: { primer: P.STAGGERED },
      heavy: { hit: { radius: 24, halfAngle: 1.35 }, primer: P.STAGGERED },
    },
  }));

  out.push(make('melee', {
    id: 'flense_dagger', cls: 'dagger', name: 'Flensing Dagger',
    // weight: authored in weapons/flense_dagger.pxm (issue #168 migration).
    reach: 11, cadenceMulBase: 5.2,
    windup: 17, active: 16, recovery: 31, cancelAfter: 0.45,
    damage: 9, poise: 0.3, type: D.PHYSICAL,
    primes: [P.BLEEDING, P.CURSED], detonates: [P.MARKED],
    terrain: terr(2, 2, 0.25, { emit: { mat: MAT.BLOOD, count: 2, velocity: 40 } }),
    slots: slots(1, 1, 2), scaling: { finesse: 1.0 },
    tags: weaponTags('FAST', 'PRIMER_TOOL', 'MOBILITY'),
    desc: 'Backstabs for ×2.5. Applies primers faster than anything else in the game.',
    combo: {
      base: { kind: 'box', radius: 11, thickness: 5, halfAngle: 0.5 },
      light1: { primer: P.BLEEDING },
      light2: { primer: P.BLEEDING },
      light3: { primer: P.BLEEDING, dmg: 1.15 },
      heavy: { dmg: 1.7, primer: P.CURSED },
      // The dash-attack blinks past the target and lands behind it.
      dash: { mx: 260, dmg: 1.6, hit: { ox: -6, throughWalls: true } },
    },
  }));

  out.push(make('melee', {
    id: 'rend_claws', cls: 'claw', name: 'Rendcarver Claws',
    // weight: authored in weapons/rend_claws.pxm (issue #168 migration).
    reach: 12, cadenceMulBase: 4.4,
    windup: 19, active: 18, recovery: 37, cancelAfter: 0.5,
    damage: 8, poise: 0.4,
    primes: [P.BLEEDING], detonates: [P.BLEEDING],
    terrain: terr(2, 3, 0.35),
    slots: slots(2, 1, 1), scaling: { finesse: 0.8, might: 0.3 },
    tags: weaponTags('FAST', 'MOBILITY', 'DETONATOR'),
    desc: 'Five-hit flurry. Shreds chitin and flesh; the wall-cling attack keeps you off the floor.',
    combo: {
      base: { kind: 'arc', radius: 12, thickness: 7, halfAngle: 0.85 },
      light1: { primer: P.BLEEDING },
      light2: { primer: P.BLEEDING },
      // Light 3 is the flurry: long active window, many contacts.
      light3: { am: 2.6, dmg: 0.55, primer: P.BLEEDING },
      air: { am: 2.0, dmg: 0.6, grav: 0.35 },
    },
  }));

  out.push(make('melee', {
    id: 'sunder_gauntlet', cls: 'gauntlet', name: 'Sundering Gauntlet',
    // weight: authored in weapons/sunder_gauntlet.pxm (issue #168 migration).
    reach: 8, cadenceMulBase: 3.6,
    windup: 23, active: 18, recovery: 42,
    damage: 11, poise: 1.2,
    primes: [P.STAGGERED],
    // The gauntlet detonates everything — that is its whole class identity.
    detonates: [P.BURNING, P.FROZEN, P.SHOCKED, P.CORRODED, P.BLEEDING, P.INFECTED, P.OILED, P.CURSED, P.IRRADIATED, P.MARKED, P.PHASED, P.STAGGERED],
    terrain: terr(4, 4, 0.6, { displaces: true }),
    slots: slots(2, 1, 2), scaling: { might: 0.9 },
    tags: weaponTags('DETONATOR', 'PARRY', 'FAST'),
    parry: { windowMs: 160, recoveryMs: 140, counterNode: 'sunder_gauntlet:hv', reflects: false, poiseGain: 2.0, primerApplied: P.STAGGERED },
    desc: 'Detonates any primer by default. Parry into counter is the intended loop.',
    combo: {
      base: { kind: 'circle', ox: 9, radius: 9, thickness: 9, halfAngle: 0 },
      heavy: { hit: { radius: 16 }, dmg: 2.4, primer: P.STAGGERED },
    },
  }));

  out.push(make('melee', {
    id: 'pike_spear', cls: 'spear', name: 'Vaulting Pike',
    // weight: authored in weapons/pike_spear.pxm (issue #168 migration).
    reach: 34, cadenceMulBase: 2.3,
    windup: 34, active: 23, recovery: 60,
    damage: 18, poise: 0.9,
    primes: [P.STAGGERED], detonates: [P.FROZEN],
    terrain: terr(3, 3, 0.4),
    slots: slots(1, 2, 1), scaling: { finesse: 0.6, might: 0.5 },
    tags: weaponTags('REACH', 'PIERCING', 'MOBILITY'),
    desc: 'Thrusts hold position instead of stepping. Planting it in terrain gives a vault.',
    combo: {
      base: { kind: 'line', ox: 17, radius: 34, thickness: 5, halfAngle: 0 },
      // A thrust that steps forward loses the spacing that makes a spear a spear.
      light1: { mx: 8 }, light2: { mx: 8 }, light3: { mx: 14, primer: P.STAGGERED },
      heavy: { hit: { radius: 46 }, mx: 90, dmg: 2.3, primer: P.STAGGERED },
      dair: { hit: { radius: 30, angle: 1.57 }, my: 230 },
    },
  }));

  out.push(make('melee', {
    id: 'charger_lance', cls: 'lance', name: 'Charger\'s Lance',
    rarity: 'rare',
    // weight: authored in weapons/charger_lance.pxm (issue #168 migration).
    reach: 46, cadenceMulBase: 0.9,
    windup: 60, active: 36, recovery: 120, cancelAfter: 0.75,
    damage: 52, poise: 2.6,
    primes: [P.STAGGERED], detonates: [P.STAGGERED],
    terrain: terr(5, 6, 0.7),
    slots: slots(2, 1, 1), scaling: { might: 1.1 },
    tags: weaponTags('REACH', 'HEAVY', 'PIERCING', 'MOBILITY'),
    desc: 'Only truly usable mid-dash. Enormous damage, enormous commitment.',
    combo: {
      base: { kind: 'line', ox: 24, radius: 46, thickness: 7, halfAngle: 0 },
      // Grounded lights are deliberately bad; the dash node is the weapon.
      light1: { dmg: 0.45, mx: 10 },
      light2: { dmg: 0.5, mx: 10 },
      light3: { dmg: 0.7, mx: 20, primer: P.STAGGERED },
      dash: { dmg: 3.2, mx: 320, am: 1.8, hit: { radius: 58 }, primer: P.STAGGERED },
    },
  }));

  out.push(make('melee', {
    id: 'iron_greatblade', cls: 'greatblade', name: 'Iron Greatblade',
    // weight: authored in weapons/iron_greatblade.pxm (issue #168 migration).
    reach: 30, cadenceMulBase: 1.0,
    windup: 66, active: 38, recovery: 104, cancelAfter: 0.7,
    damage: 46, poise: 3.0,
    primes: [P.STAGGERED, P.BLEEDING], detonates: [P.FROZEN],
    terrain: terr(4, 9, 0.85, { displaces: true }),
    slots: slots(1, 2, 1), scaling: { might: 1.2 },
    tags: weaponTags('HEAVY', 'AOE', 'DIGGER', 'REACH'),
    desc: 'Cleaves a visible channel through anything soft. Cancels enemy poise outright.',
    combo: {
      base: { kind: 'arc', radius: 30, thickness: 14, halfAngle: 1.5 },
      light3: { primer: P.BLEEDING },
      heavy: { hit: { radius: 38, halfAngle: 1.9 }, dmg: 2.6, primer: P.STAGGERED },
      dair: { hit: { radius: 26, angle: 1.4 }, my: 260 },
    },
  }));

  out.push(make('melee', {
    id: 'shatter_hammer', cls: 'hammer', name: 'Shatterfall Hammer',
    // weight: authored in weapons/shatter_hammer.pxm (issue #168 migration).
    reach: 22, cadenceMulBase: 0.7,
    windup: 84, active: 32, recovery: 120, cancelAfter: 0.8,
    damage: 58, poise: 4.0,
    primes: [P.STAGGERED],
    detonates: [P.FROZEN, P.STAGGERED, P.CORRODED],
    terrain: terr(5, 11, 1.0, { displaces: true }),
    slots: slots(1, 2, 1), scaling: { might: 1.4 },
    tags: weaponTags('HEAVY', 'AOE', 'DIGGER', 'DETONATOR'),
    desc: 'Shatters FROZEN into a cone. Destroys anything of hardness 5 or less.',
    combo: {
      base: { kind: 'circle', ox: 18, radius: 16, thickness: 16, halfAngle: 0 },
      light3: { primer: P.STAGGERED },
      heavy: { hit: { kind: 'ring', radius: 40, thickness: 6 }, dmg: 2.8, primer: P.STAGGERED },
      dair: { hit: { radius: 24 }, my: 300, dmg: 1.9 },
    },
  }));

  out.push(make('melee', {
    id: 'cleaver_axe', cls: 'axe', name: 'Grovecleaver Axe',
    // weight: authored in weapons/cleaver_axe.pxm (issue #168 migration).
    reach: 19, cadenceMulBase: 1.7,
    windup: 44, active: 29, recovery: 76,
    damage: 28, poise: 1.6,
    primes: [P.BLEEDING], detonates: [P.BLEEDING],
    terrain: terr(4, 6, 0.9, { emit: { mat: MAT.ASH, count: 3, velocity: 30 } }),
    slots: slots(1, 2, 1), scaling: { might: 0.9, finesse: 0.2 },
    tags: weaponTags('DIGGER', 'AOE', 'DETONATOR'),
    desc: 'Bleeds on every hit, chews through wood and organics, and fells what it bites into.',
    combo: {
      base: { kind: 'arc', radius: 19, thickness: 11, halfAngle: 1.25 },
      light1: { primer: P.BLEEDING }, light2: { primer: P.BLEEDING },
      light3: { primer: P.BLEEDING },
      // Heavy is the felling stroke: the same arc, taken with both hands and
      // committed to — wider, deeper, and slower to come back from.
      //
      // It used to be the catalogue's one thrown heavy, and that is #100. `down`
      // both selects the heavy entry and aims the attack, so the throw was only
      // reachable pointed at the floor: measured on the ground, the round was
      // born inside terrain and `projectiles.count` was 0. The throw now lives
      // on `dredge_anchor`'s **light** node, which no vertical press selects, so
      // nothing about aiming had to change and this node can go back to being
      // what an axe's heavy should always have been. A cleaver aimed down still
      // digs, which is the traversal verb the reverted fix (6baa75d) cost.
      heavy: { hit: { radius: 26, thickness: 16, halfAngle: 1.6 }, dmg: 2.4, primer: P.BLEEDING },
    },
  }));

  out.push(make('melee', {
    id: 'reaper_scythe', cls: 'scythe', name: 'Reaper\'s Harvest',
    rarity: 'uncommon',
    // weight: authored in weapons/reaper_scythe.pxm (issue #168 migration).
    reach: 28, cadenceMulBase: 1.6,
    windup: 47, active: 34, recovery: 74,
    damage: 26, poise: 1.4, type: D.BLOOD,
    primes: [P.BLEEDING, P.CURSED], detonates: [P.BLEEDING, P.CURSED],
    terrain: terr(3, 7, 0.5, { emit: { mat: MAT.BLOOD, count: 6, velocity: 55 } }),
    slots: slots(1, 2, 2), scaling: { might: 0.6, resonance: 0.5 },
    tags: weaponTags('REACH', 'AOE', 'DETONATOR'),
    desc: 'Hits behind you on every swing, and harvests corpses into pooled BLOOD.',
    combo: {
      // Full-circle arcs are the class identity: there is no safe side.
      base: { kind: 'arc', radius: 28, thickness: 10, halfAngle: 2.6 },
      light3: { primer: P.BLEEDING },
      heavy: { hit: { halfAngle: 3.14, radius: 32 }, dmg: 2.3, primer: P.CURSED },
    },
  }));

  out.push(make('melee', {
    id: 'tether_whip', cls: 'whip', name: 'Conducting Tether',
    // weight: authored in weapons/tether_whip.pxm (issue #168 migration).
    reach: 52, cadenceMulBase: 2.6,
    windup: 28, active: 25, recovery: 51,
    damage: 12, poise: 0.5, type: D.SHOCK,
    primes: [P.SHOCKED], detonates: [P.OILED, P.SHOCKED],
    terrain: terr(2, 3, 0.3, { charge: 0.7 }),
    slots: slots(2, 1, 1), scaling: { finesse: 0.7, tech: 0.4 },
    tags: weaponTags('REACH', 'MOBILITY', 'UTILITY', 'PRIMER_TOOL'),
    desc: 'Grabs items, tethers to anchors, and conducts charge down its whole length.',
    combo: {
      base: { kind: 'line', ox: 26, radius: 52, thickness: 4, halfAngle: 0, throughWalls: true },
      light1: { primer: P.SHOCKED }, light2: { primer: P.SHOCKED },
      light3: { primer: P.SHOCKED },
      // The dash node is the tether-swing: it pulls you, not the target.
      dash: { mx: 230, my: -120, dmg: 0.8 },
    },
  }));

  out.push(make('melee', {
    id: 'flail_chain', cls: 'chain', name: 'Ballast Chain',
    // weight: authored in weapons/flail_chain.pxm (issue #168 migration).
    reach: 26, cadenceMulBase: 1.2,
    windup: 52, active: 44, recovery: 88, cancelAfter: 0.65,
    damage: 30, poise: 2.0,
    primes: [P.STAGGERED], detonates: [P.STAGGERED],
    terrain: terr(3, 8, 0.6, { displaces: true }),
    slots: slots(2, 2, 0), scaling: { might: 1.0 },
    tags: weaponTags('AOE', 'HEAVY', 'CHARGEABLE'),
    chargeStates: [
      { id: 'spin1', holdMs: 300, damageMul: 1.2, radiusMul: 1.25 },
      { id: 'spin2', holdMs: 750, damageMul: 1.6, radiusMul: 1.7, primerApplied: PrimerKind.STAGGERED },
      { id: 'spin3', holdMs: 1400, damageMul: 2.4, radiusMul: 2.3, releaseNode: 'flail_chain:hv' },
    ],
    desc: 'Spin-up grows the radius and flings every loose cell it touches.',
    combo: {
      base: { kind: 'ring', radius: 26, thickness: 10, halfAngle: 3.14 },
      heavy: { hit: { radius: 44, thickness: 14 }, dmg: 2.5, am: 2.2, primer: P.STAGGERED },
    },
  }));

  out.push(make('melee', {
    id: 'bulwark_shield', cls: 'shield', name: 'Tidebreaker Bulwark',
    // weight: authored in weapons/bulwark_shield.pxm (issue #168 migration).
    reach: 12, cadenceMulBase: 1.1,
    windup: 40, active: 24, recovery: 84,
    // 12 -> 25 (Phase 7, docs/15 §3). The upper of the band's two outliers, at
    // 36.0 / 33.6 dps against a floor of 68.7. Same dial and same rule as
    // `auger_drill`: the node multipliers below (0.5 / 0.6 / 0.9 light, 1.8
    // heavy) are this weapon's own shape — a bash is a fraction of a bash-heavy
    // — and `damage` is what says how hard it hits at all. 25 is the value that
    // clears the floor on the **hitstop-paid** reading (69.98), which is the
    // binding one here because the shield pays a 7% freeze tax the drill does
    // not; the excluded reading lands at 75.1. What a shield is allowed to buy
    // with its weakness is parry, poise 3.5 and planing across liquids, which
    // §3 lists as legitimate; it is not allowed to buy 8.6x.
    damage: 25, poise: 3.5,
    primes: [P.STAGGERED], detonates: [P.STAGGERED],
    terrain: terr(3, 5, 0.3, { displaces: true }),
    slots: slots(1, 1, 2), scaling: { vitality: 1.0, might: 0.3 },
    tags: weaponTags('PARRY', 'OFFHAND_FIRST', 'UTILITY', 'MOBILITY'),
    offhandFirst: true,
    parry: { windowMs: 220, recoveryMs: 160, counterNode: 'bulwark_shield:hv', reflects: true, poiseGain: 3.0, primerApplied: P.STAGGERED },
    desc: 'Off-hand only. Blocks, parries, bashes — and planes across liquids at speed.',
    combo: {
      base: { kind: 'box', ox: 11, radius: 10, thickness: 12, halfAngle: 0 },
      light1: { dmg: 0.5 }, light2: { dmg: 0.6 }, light3: { dmg: 0.9 },
      heavy: { dmg: 1.8, mx: 170, primer: P.STAGGERED },
      // Surfing: the down-air planes rather than dropping.
      dair: { my: 90, mx: 120, grav: 0.4, dmg: 1.0 },
    },
  }));

  out.push(make('melee', {
    id: 'conduit_staff', cls: 'staff', name: 'Conduit Staff',
    // weight: authored in weapons/conduit_staff.pxm (issue #168 migration).
    reach: 24, cadenceMulBase: 2.8,
    windup: 26, active: 22, recovery: 48,
    damage: 11, poise: 0.7, type: D.RADIANT,
    primes: [P.MARKED], detonates: [P.MARKED],
    // 2 -> 4 (#132, docs/15 §3.2). Radiant is VOIDGLASS's only key (3.00, the
    // top of the whole table) and CHOIRLIGHT's and CORPSE_STONE's; at 2 the
    // doubled edge was 4 and reached none of them. A key that cannot reach its
    // own lock is not a key.
    terrain: terr(4, 4, 0.25),
    slots: slots(1, 3, 1), scaling: { focus: 0.9, resonance: 0.6 },
    tags: weaponTags('OFFHAND_FIRST', 'UTILITY', 'PRIMER_TOOL'),
    offhandFirst: true,
    desc: 'Marks targets and buffs the other hand for six seconds. Damage is not the point.',
    combo: {
      base: { kind: 'arc', radius: 24, thickness: 8, halfAngle: 1.0 },
      light1: { primer: P.MARKED }, light2: { primer: P.MARKED },
      light3: { primer: P.MARKED },
      heavy: { dmg: 1.5, hit: { kind: 'ring', radius: 40, thickness: 12, halfAngle: 3.14 }, primer: P.MARKED },
    },
  }));

  out.push(make('melee', {
    id: 'echo_relic_blade', cls: 'relicBlade', name: 'Echo of the First Blade',
    rarity: 'relic',
    // weight: authored in weapons/echo_relic_blade.pxm (issue #168 migration).
    reach: 22, cadenceMulBase: 1.9,
    windup: 38, active: 30, recovery: 70,
    damage: 24, poise: 1.2, type: D.TEMPORAL,
    primes: [P.MARKED, P.PHASED], detonates: [P.PHASED, P.MARKED],
    terrain: terr(4, 6, 0.5, { heat: 40 }),
    slots: slots(1, 2, 2), scaling: { resonance: 1.3 },
    tags: weaponTags('RESONANT', 'DETONATOR'),
    desc: 'Scales on resonance and rewrites its own moveset when the biome changes.',
    combo: {
      base: { kind: 'arc', radius: 22, thickness: 10, halfAngle: 1.2 },
      light3: { primer: P.PHASED },
      heavy: { dmg: 2.4, hit: { radius: 30, throughWalls: true }, primer: P.MARKED },
    },
  }));

  out.push(make('melee', {
    id: 'auger_drill', cls: 'drill', name: 'Deepcut Auger',
    // weight: authored in weapons/auger_drill.pxm (issue #168 migration).
    reach: 14, cadenceMulBase: 8.0,
    windup: 52, active: 168, recovery: 64, cancelAfter: 0.4,
    // 4 -> 18 (Phase 7, docs/15 §3). The lower of the band's two outliers, at
    // 15.4 dps against a floor of 68.7. The shortfall was entirely in what a
    // contact is worth, not in how often one lands: the grind lands 7 contacts
    // per chain in 919 ms — 7.6 Hz, a fast-melee rate — and each was worth 2.09
    // against the catalogue floor's 9.15. `damage` is the dial because the node
    // multipliers below (0.5 / 0.5 / 0.55) are a statement about this weapon's
    // own shape — a tick of a grind against its own heavy — and the price of
    // being continuous is authored as fuel and heat, not as output.
    damage: 18, poise: 0.6,
    primes: [P.STAGGERED], detonates: [P.CORRODED],
    // 6 -> 7 (#132, docs/15 §3.2). The force ceiling is the route of last
    // resort, so it has to reach the top of the band that has no key: GRANITE,
    // RUNESTONE and OBSIDIAN all sit at 7 and `typeEffectivenessVs` gives none
    // of them a damage type a shipped weapon carries. 7 rather than 8 because
    // STEEL and PLASTEEL must stay off it — they have keys (shock, nanite) and
    // a force ladder that reached them would make every route decorative.
    terrain: terr(7, 5, 1.0, { heat: 55, emit: { mat: MAT.GRAVEL, count: 4, velocity: 70 } }),
    slots: slots(2, 1, 1), scaling: { tech: 0.9, might: 0.4 },
    tags: weaponTags('CONTINUOUS', 'DIGGER', 'SELF_RISK'),
    heatPerSecond: 0.3,
    desc: 'Continuous. Eats fuel, heats up, and will cook you if you never let go.',
    combo: {
      base: { kind: 'box', ox: 12, radius: 12, thickness: 6, halfAngle: 0 },
      // Every node is a long grind rather than a discrete swing.
      light1: { am: 1.0, dmg: 0.5 }, light2: { am: 1.0, dmg: 0.5 },
      light3: { am: 1.4, dmg: 0.55 },
      heavy: { am: 2.0, dmg: 0.9, hit: { radius: 18 }, primer: P.STAGGERED },
      dair: { am: 1.6, my: 210, dmg: 0.8 },
    },
  }));

  out.push(make('melee', {
    id: 'hunger_symbiote', cls: 'living', name: 'Hungering Symbiote',
    rarity: 'exotic',
    // weight: authored in weapons/hunger_symbiote.pxm (issue #168 migration).
    reach: 18, cadenceMulBase: 2.4,
    windup: 35, active: 27, recovery: 64,
    damage: 20, poise: 1.0, type: D.FUNGAL,
    primes: [P.INFECTED, P.BLEEDING], detonates: [P.INFECTED],
    terrain: terr(3, 5, 0.45, { emit: { mat: MAT.FUNGAL_MAT, count: 4, velocity: 25 } }),
    slots: slots(1, 2, 2), scaling: { vitality: 0.6, resonance: 0.6 },
    tags: weaponTags('ORGANIC', 'SELF_RISK', 'PRIMER_TOOL'),
    desc: 'It is hungry. Feed it kills or it feeds on you, and its moveset mutates either way.',
    combo: {
      base: { kind: 'arc', radius: 18, thickness: 10, halfAngle: 1.15 },
      light2: { primer: P.INFECTED }, light3: { primer: P.INFECTED },
      heavy: { dmg: 2.2, hit: { radius: 26, thickness: 14 }, primer: P.INFECTED },
    },
  }));

  /**
   * The eighteenth melee class, and the only weapon whose **opener** leaves
   * the hand — issue #100.
   *
   * ### Why it exists
   *
   * `down` selects the heavy entry *and* aims the attack. Those are the same
   * press, so a throw authored on a heavy is a throw at the floor and nothing
   * else; `cleaver_axe` measured `projectiles.count === 0` on the ground. The
   * two candidate fixes both cost more than they bought — level-aiming grounded
   * heavies removes downward digging from every weapon at once (tried,
   * reverted in 6baa75d, nine tests), and no predicate distinguishes a thrown
   * heavy from an ordinary ranged weapon, because ordinary ranged weapons are
   * `spawn` hitboxes too.
   *
   * So the throw moves to an entry `down` does not select. That is a catalogue
   * edit and not a rule change: `pickEntryKind`, `state.aim` and every aim test
   * are untouched, and the axe's heavy goes back to being a swing.
   *
   * ### The identity, which is not a re-skinned axe
   *
   * Cast, haul, bite. The chain is the weapon and the head is the payload:
   *
   *   - `l1` **the cast** — the only *light* node in the game with a `spawn`
   *     hitbox. Slow (240 u/s against the axe's old 340), heavy, and it falls
   *     while it flies. Aimed wherever the player points, including down, and
   *     down is now a choice rather than the only option.
   *   - `l2` **the haul** — the chain dragged back through everything between
   *     the head and the hand: a 44-unit `line`, the longest melee shape here
   *     after the whip's. Anything light enough comes back with it, which is
   *     `applyGrab`'s second class (wielder.ts).
   *   - `l3` **the bite** — the head arriving. Short, wide, `STAGGERED`.
   *   - `hv` **the drop** — down-gated, and honestly so: a hundredweight let go
   *     straight down is what the heavy of an anchor *should* be, so the entry
   *     whose input also aims it downward is the right home rather than a
   *     compromise. Displaces terrain; this is the digger of the pair.
   *   - `da` the drag, `ar` the swung head, `dr` the drop again with a pogo.
   *
   * ### The interaction it deliberately avoids
   *
   * `tether_whip` reels its **wielder** toward anchor-mass bodies, and
   * `pickEntryKind` promotes any press above 200 u/s to the dash entry — so the
   * whip feeds its own entry selector and 285 of 287 of its landed hits are one
   * node (#50, docs/15 §8). A weapon whose fiction is "haul" is the obvious
   * next victim. This one hauls in the other direction only: `applyGrab` never
   * writes the wielder's velocity for an `anchor`, an anchor-mass target simply
   * refuses to be moved, and `tests/anchorThrow.test.ts` §4 measures the node
   * spread rather than asserting the intent.
   */
  out.push(make('melee', {
    id: 'dredge_anchor', cls: 'anchor', name: 'Dredging Anchor',
    rarity: 'uncommon',
    // weight: authored in weapons/dredge_anchor.pxm (issue #168 migration).
    reach: 40, cadenceMulBase: 0.95,
    windup: 58, active: 30, recovery: 92, cancelAfter: 0.75,
    damage: 34, poise: 3.2,
    primes: [P.STAGGERED], detonates: [P.STAGGERED, P.CORRODED],
    terrain: terr(5, 7, 0.75, { displaces: true }),
    slots: slots(2, 1, 1), scaling: { might: 1.2, finesse: 0.2 },
    tags: weaponTags('THROWN', 'HEAVY', 'REACH', 'DIGGER'),
    desc: 'Cast it out, haul it back, and everything it caught comes with it.',
    combo: {
      // The chain, at rest: a long line from the hand out to the head.
      base: { kind: 'line', ox: 18, radius: 40, thickness: 9, halfAngle: 0 },
      // The cast. A `spawn` hitbox with a `FireSpec` is the existing hand-off
      // (#93) — no new field, no detachment flag on `SwingStyle`.
      light1: {
        hit: { kind: 'spawn', ox: 10, radius: 12, thickness: 12 },
        dmg: 1.5, mx: 8,
        // Slow and falling: 280 u/s against the axe's old 340, and it arcs hard
        // enough that a level cast from a standing wielder is in the floor
        // about 70 units out — comfortably past the 40-unit chain, and well
        // short of a rifle. Range is the thing the cast buys and the arc is
        // what it pays.
        fire: { count: 1, speed: 280, lifetime: 1.6, pierce: 1, gravityScale: 0.35 },
      },
      // The haul: the whole chain length is live on the way back in.
      light2: { hit: { radius: 44, thickness: 12 }, dmg: 1.2, mx: 12 },
      // The bite: the head arriving at the hand.
      light3: {
        hit: { kind: 'arc', ox: 14, radius: 24, thickness: 15, halfAngle: 1.2 },
        dmg: 1.6, mx: 26, primer: P.STAGGERED,
      },
      // The drop. Down-gated, aimed down by the same press, and that is right.
      heavy: {
        hit: { kind: 'circle', ox: 10, radius: 24, thickness: 24, halfAngle: 0 },
        dmg: 2.5, primer: P.STAGGERED,
      },
      dash: { hit: { radius: 46, thickness: 12 }, dmg: 1.4 },
      dair: { hit: { kind: 'circle', radius: 22, thickness: 22, halfAngle: 0 }, my: 300, dmg: 1.8 },
    },
  }));

  // ------------------------------------------------------- projectile (12)
  //
  // Every class here carries a magazine, and the number that matters is not the
  // magazine — it is the fraction of the fight the weapon is actually shooting,
  // `(magazine / ammoCostOf) × node ms : reloadMs`.
  //
  // The 63% (launcher) to 85% (sprayer) band this comment used to state was
  // computed as `magazine / cadence`, i.e. off `cadenceMulBase`, which reaches
  // no timing at all. Measured off the node triple the launcher is 18.1% and
  // the drone 16.4%, so the published band is an artifact of the dead field
  // rather than a description of the game. **Do not re-derive it from here.**
  // Phase 3 / #39 chooses a real band; `tests/balance.test.ts` measures the
  // honest number in the meantime. The *shape* of the claim survives — uptime
  // should slope against impact — and that half is now tested directly.
  //
  // Tuning against damage instead would just
  // reprice the same dominance, which is the point issue #13 makes — a
  // projectile with no cost is strictly better than melee at range, and then
  // there is no reason to ever close.
  //
  // Melee has no magazine on purpose. Its cost is that it has to be in the
  // room, and a connected melee hit hands a round back to the other hand
  // (§8's "melee refreshes projectile ammo on hit"), which is what makes the
  // mixed pair "spend the magazine, then close" rather than "shoot forever".

  out.push(make('projectile', {
    id: 'service_pistol', cls: 'pistol', name: 'Service Repeater',
    // weight: authored in weapons/service_pistol.pxm (issue #168 migration).
    reach: 260, cadenceMulBase: 5.0,
    windup: 12, active: 8, recovery: 32, cancelAfter: 0.3,
    damage: 8, poise: 0.3,
    primes: [P.MARKED], detonates: [P.OILED],
    terrain: terr(2, 2, 0.3),
    slots: slots(1, 1, 2), scaling: { finesse: 0.7, tech: 0.3 },
    tags: weaponTags('FAST', 'OFFHAND_FIRST', 'DETONATOR'),
    offhandFirst: true,
    magazine: 20, reloadMs: 680, heatPerShot: 0.045,
    desc: 'The dependable off-hand. Cancels into everything and commits to nothing.',
    combo: {
      base: { kind: 'spawn', ox: 10, radius: 4, thickness: 4, halfAngle: 0 },
      baseFire: { count: 1, speed: 620, lifetime: 0.9, recoil: 20 },
      heavy: { dmg: 1.8, primer: P.MARKED, fire: { count: 2, spread: 0.06, speed: 700, recoil: 55 } },
    },
  }));

  out.push(make('projectile', {
    id: 'longbore_rifle', cls: 'rifle', name: 'Longbore Rifle',
    // weight: authored in weapons/longbore_rifle.pxm (issue #168 migration).
    reach: 640, cadenceMulBase: 1.1,
    windup: 56, active: 12, recovery: 88, cancelAfter: 0.55,
    damage: 40, poise: 1.5,
    primes: [P.MARKED], detonates: [P.MARKED],
    terrain: terr(3, 2, 0.8),
    slots: slots(1, 2, 1), scaling: { finesse: 1.0, focus: 0.3 },
    tags: weaponTags('PIERCING', 'REACH', 'CHARGEABLE'),
    magazine: 12, reloadMs: 1860, heatPerShot: 0.07,
    chargeStates: [
      { id: 'steady', holdMs: 350, damageMul: 1.4, radiusMul: 1 },
      { id: 'bore', holdMs: 900, damageMul: 2.2, radiusMul: 1, primerApplied: PrimerKind.MARKED },
    ],
    desc: 'Charge to pierce. Three enemies and any thin wall between them.',
    combo: {
      base: { kind: 'spawn', ox: 14, radius: 4, thickness: 3, halfAngle: 0 },
      baseFire: { count: 1, speed: 1150, lifetime: 1.1, pierce: 3, recoil: 90 },
      heavy: { dmg: 2.4, primer: P.MARKED, fire: { pierce: 6, speed: 1400, recoil: 160 } },
    },
  }));

  out.push(make('projectile', {
    id: 'breach_scattergun', cls: 'scattergun', name: 'Breachmaker',
    // weight: authored in weapons/breach_scattergun.pxm (issue #168 migration).
    reach: 130, cadenceMulBase: 1.0,
    windup: 28, active: 10, recovery: 100, cancelAfter: 0.6,
    damage: 7, poise: 1.8,
    primes: [P.STAGGERED], detonates: [P.OILED, P.STAGGERED],
    terrain: terr(3, 7, 0.7, { displaces: true }),
    slots: slots(2, 1, 1), scaling: { might: 0.6, finesse: 0.4 },
    tags: weaponTags('AOE', 'MOBILITY', 'DETONATOR'),
    magazine: 11, reloadMs: 1630, heatPerShot: 0.08,
    desc: 'The recoil is the point. Firing downward is a double jump that also kills things.',
    combo: {
      base: { kind: 'spawn', ox: 11, radius: 5, thickness: 5, halfAngle: 0 },
      baseFire: { count: 9, spread: 0.34, speed: 540, lifetime: 0.28, recoil: 300 },
      heavy: { dmg: 1.6, primer: P.STAGGERED, fire: { count: 14, spread: 0.5, recoil: 520 } },
      dair: { fire: { count: 12, recoil: 620 }, my: 120 },
    },
  }));

  out.push(make('projectile', {
    id: 'siege_launcher', cls: 'launcher', name: 'Siege Thrower',
    // weight: authored in weapons/siege_launcher.pxm (issue #168 migration).
    reach: 420, cadenceMulBase: 0.5,
    windup: 76, active: 14, recovery: 152, cancelAfter: 0.75,
    damage: 62, poise: 3.0, type: D.FIRE,
    primes: [P.BURNING, P.STAGGERED], detonates: [P.OILED, P.BURNING],
    terrain: terr(5, 22, 1.0, { heat: 320, displaces: true }),
    slots: slots(1, 3, 1), scaling: { might: 0.8, tech: 0.5 },
    tags: weaponTags('AOE', 'DIGGER', 'HEAVY', 'SELF_RISK'),
    magazine: 9, reloadMs: 2800, heatPerShot: 0.1,
    desc: 'Opens rooms. Friendly fire is real and the blast does not check your team.',
    combo: {
      base: { kind: 'spawn', ox: 13, radius: 7, thickness: 7, halfAngle: 0 },
      baseFire: { count: 1, speed: 340, lifetime: 2.4, gravityScale: 0.85, recoil: 140 },
      light3: { primer: P.STAGGERED },
      heavy: { dmg: 1.7, primer: P.BURNING, fire: { count: 3, spread: 0.22, recoil: 260 } },
    },
  }));

  out.push(make('projectile', {
    id: 'silent_bow', cls: 'bow', name: 'Silent Recurve',
    // weight: authored in weapons/silent_bow.pxm (issue #168 migration).
    reach: 480, cadenceMulBase: 1.4,
    windup: 48, active: 10, recovery: 60, cancelAfter: 0.5,
    damage: 26, poise: 0.8,
    primes: [P.BLEEDING, P.MARKED], detonates: [P.MARKED],
    terrain: terr(2, 2, 0.4),
    slots: slots(1, 2, 2), scaling: { finesse: 1.1 },
    tags: weaponTags('CHARGEABLE', 'REACH', 'PRIMER_TOOL'),
    magazine: 14, reloadMs: 1280,
    chargeStates: [
      { id: 'draw', holdMs: 260, damageMul: 1.35, radiusMul: 1 },
      { id: 'full', holdMs: 620, damageMul: 2.0, radiusMul: 1, primerApplied: PrimerKind.BLEEDING },
      { id: 'over', holdMs: 1200, damageMul: 2.6, radiusMul: 1 },
    ],
    desc: 'Arrows are objects. They stick in walls, they stick in enemies, and you can pull them back out.',
    combo: {
      base: { kind: 'spawn', ox: 12, radius: 4, thickness: 3, halfAngle: 0 },
      baseFire: { count: 1, speed: 700, lifetime: 3.0, gravityScale: 0.28, pierce: 1, recoil: 15 },
      light1: { primer: P.MARKED }, light2: { primer: P.MARKED },
      light3: { primer: P.MARKED },
      heavy: { dmg: 2.2, primer: P.BLEEDING, fire: { speed: 980, pierce: 3 } },
    },
  }));

  out.push(make('projectile', {
    id: 'ambient_rail', cls: 'rail', name: 'Ambient Rail',
    rarity: 'rare',
    // weight: authored in weapons/ambient_rail.pxm (issue #168 migration).
    reach: 1400, cadenceMulBase: 0.35,
    windup: 120, active: 16, recovery: 168, cancelAfter: 0.8,
    damage: 85, poise: 2.5, type: D.SHOCK,
    primes: [P.SHOCKED], detonates: [P.SHOCKED, P.OILED],
    terrain: terr(5, 3, 1.0, { charge: 1.0, heat: 90 }),
    slots: slots(1, 2, 1), scaling: { tech: 1.2 },
    tags: weaponTags('PIERCING', 'REACH', 'HEAVY', 'CHARGEABLE'),
    magazine: 3, reloadMs: 1070, heatPerShot: 0.3,
    chargeStates: [
      { id: 'bank', holdMs: 500, damageMul: 1.5, radiusMul: 1 },
      { id: 'full', holdMs: 1300, damageMul: 2.6, radiusMul: 1, primerApplied: PrimerKind.SHOCKED },
    ],
    desc: 'Draws its charge from ambient electricity — standing in a storm is a reload.',
    combo: {
      base: { kind: 'spawn', ox: 15, radius: 5, thickness: 4, halfAngle: 0 },
      baseFire: { count: 1, speed: 2600, lifetime: 0.7, pierce: 99, recoil: 200 },
      heavy: { dmg: 2.0, primer: P.SHOCKED, fire: { speed: 3200, recoil: 340 } },
    },
  }));

  out.push(make('projectile', {
    id: 'prism_shardcaster', cls: 'shardCaster', name: 'Prism Shardcaster',
    // weight: authored in weapons/prism_shardcaster.pxm (issue #168 migration).
    reach: 300, cadenceMulBase: 4.0,
    windup: 18, active: 8, recovery: 37, cancelAfter: 0.4,
    damage: 6, poise: 0.4, type: D.FROST,
    primes: [P.FROZEN], detonates: [P.FROZEN],
    terrain: terr(3, 3, 0.5, { heat: -70, emit: { mat: MAT.CRYSTAL, count: 2, velocity: 90 } }),
    emits: { mat: MAT.CRYSTAL, count: 2, spread: 0.3 },
    slots: slots(2, 2, 0), scaling: { focus: 0.8, resonance: 0.4 },
    tags: weaponTags('FAST', 'PRIMER_TOOL'),
    magazine: 18, reloadMs: 810, heatPerShot: 0.05,
    desc: 'Shards ricochet, then persist as crystal cells you can shatter later.',
    combo: {
      base: { kind: 'spawn', ox: 10, radius: 4, thickness: 4, halfAngle: 0 },
      baseFire: { count: 2, spread: 0.1, speed: 520, lifetime: 1.6, gravityScale: 0.15, recoil: 25 },
      light1: { primer: P.FROZEN }, light2: { primer: P.FROZEN },
      light3: { primer: P.FROZEN },
      heavy: { dmg: 1.7, primer: P.FROZEN, fire: { count: 6, spread: 0.4 } },
    },
  }));

  out.push(make('projectile', {
    id: 'kiln_beam', cls: 'beam', name: 'Kiln Emitter',
    // weight: authored in weapons/kiln_beam.pxm (issue #168 migration).
    reach: 240, cadenceMulBase: 12.0,
    windup: 36, active: 200, recovery: 72, cancelAfter: 0.45,
    damage: 3, poise: 0.2, type: D.FIRE,
    primes: [P.BURNING], detonates: [P.OILED, P.BURNING],
    terrain: terr(5, 2, 1.0, { heat: 420 }),
    slots: slots(1, 2, 1), scaling: { focus: 1.0, tech: 0.4 },
    tags: weaponTags('CONTINUOUS', 'DIGGER', 'PIERCING'),
    magazine: 60, reloadMs: 1800, heatPerSecond: 0.26,
    desc: 'Melts a precise line, refracts through crystal, and lights the cave while it works.',
    combo: {
      base: { kind: 'line', ox: 12, radius: 240, thickness: 4, halfAngle: 0 },
      light1: { dmg: 0.4 }, light2: { dmg: 0.4 }, light3: { dmg: 0.5, am: 1.4, primer: P.BURNING },
      heavy: { dmg: 0.9, am: 2.0, hit: { thickness: 9 }, primer: P.BURNING },
      dair: { hit: { angle: 1.57, radius: 120 }, my: 150, am: 1.5, dmg: 0.5 },
    },
  }));

  out.push(make('projectile', {
    id: 'warden_drones', cls: 'drone', name: 'Warden Flock',
    rarity: 'uncommon',
    // weight: authored in weapons/warden_drones.pxm (issue #168 migration).
    reach: 200, cadenceMulBase: 0.8,
    windup: 44, active: 16, recovery: 80, cancelAfter: 0.4,
    damage: 8, poise: 0.4, type: D.NANITE,
    primes: [P.MARKED], detonates: [P.MARKED],
    // 2 -> 5 (#132, docs/15 §3.2). Nanite against PLASTEEL is 2.60 — the
    // highest score any type gets against any material — and PLASTEEL at
    // hardness 9 is the one bulk layer above the force ceiling. The flock is
    // where that key lives, so it is the rung that has to reach it.
    terrain: terr(5, 2, 0.2),
    slots: slots(1, 2, 2), scaling: { tech: 1.1 },
    tags: weaponTags('SUMMONER', 'OFFHAND_FIRST', 'UTILITY'),
    offhandFirst: true,
    magazine: 4, reloadMs: 450,
    desc: 'Deploys drones that fight on their own. They are entities; they can be shot down.',
    combo: {
      base: { kind: 'spawn', ox: 8, radius: 6, thickness: 6, halfAngle: 0 },
      baseFire: { count: 1, speed: 180, lifetime: 12, gravityScale: 0, recoil: 0 },
      heavy: { dmg: 1.5, primer: P.MARKED, fire: { count: 3, spread: 0.5, lifetime: 16 } },
    },
  }));

  out.push(make('projectile', {
    id: 'alchem_sprayer', cls: 'sprayer', name: 'Alchemical Sprayer',
    // weight: authored in weapons/alchem_sprayer.pxm (issue #168 migration).
    reach: 90, cadenceMulBase: 14.0,
    windup: 20, active: 184, recovery: 52, cancelAfter: 0.35,
    damage: 1.5, poise: 0.1, type: D.ACID,
    primes: [P.OILED, P.CORRODED, P.BURNING, P.FROZEN],
    detonates: [],
    // 1 -> 3 (#132, docs/15 §3.2). Acid is the key to porous stone and to
    // corroding metal — LIMESTONE 1.79, PERMAFROST 1.58, CONCRETE 1.51, IRON
    // 2.21, RUSTED_IRON 2.32 — and at 1 the doubled edge was 2, which reached
    // none of the five. 3 puts it at 6, exactly IRON's hardness and the
    // hardest thing acid opens.
    terrain: terr(3, 2, 0.1, {
      emit: { mat: MAT.OIL, count: 10, velocity: 140 },
      displaces: true,
    }),
    emits: { mat: MAT.OIL, count: 10, spread: 0.22 },
    slots: slots(1, 3, 1), scaling: { tech: 0.7, focus: 0.5 },
    tags: weaponTags('CONTINUOUS', 'MATTER_AUTHOR', 'PRIMER_TOOL', 'OFFHAND_FIRST', 'UTILITY'),
    offhandFirst: true,
    magazine: 90, reloadMs: 1600, heatPerSecond: 0.12,
    desc: 'Sprays real cells. Load it with oil, acid, water, coolant, spores, nanites, or void.',
    combo: {
      base: { kind: 'cone', ox: 10, radius: 90, thickness: 6, halfAngle: 0.3 },
      baseFire: { count: 4, spread: 0.3, speed: 260, lifetime: 0.6, gravityScale: 0.7, recoil: 8, trailMat: MAT.OIL },
      light1: { dmg: 0.3, primer: PrimerKind.OILED },
      light2: { dmg: 0.3, primer: PrimerKind.OILED },
      light3: { dmg: 0.35, am: 1.5, primer: PrimerKind.OILED },
      heavy: { dmg: 0.6, am: 2.0, hit: { radius: 120, halfAngle: 0.5 }, fire: { count: 9, spread: 0.5 } },
      dair: { hit: { angle: 1.57 }, my: 60, grav: 0.5, dmg: 0.3 },
    },
  }));

  out.push(make('projectile', {
    id: 'seedthrower', cls: 'bio', name: 'Bloomthrower',
    // weight: authored in weapons/seedthrower.pxm (issue #168 migration).
    reach: 220, cadenceMulBase: 1.8,
    windup: 38, active: 12, recovery: 68, cancelAfter: 0.5,
    damage: 14, poise: 0.6, type: D.FUNGAL,
    primes: [P.INFECTED], detonates: [P.INFECTED],
    terrain: terr(2, 4, 0.4, { emit: { mat: MAT.SPORE_DUST, count: 8, velocity: 60 } }),
    emits: { mat: MAT.FUNGAL_MAT, count: 5, spread: 0.4 },
    slots: slots(1, 3, 1), scaling: { vitality: 0.5, focus: 0.6 },
    tags: weaponTags('ORGANIC', 'MATTER_AUTHOR', 'PRIMER_TOOL'),
    magazine: 12, reloadMs: 1300,
    desc: 'Fires seeds that grow into terrain. Slow, and then suddenly the room is different.',
    combo: {
      base: { kind: 'spawn', ox: 11, radius: 5, thickness: 5, halfAngle: 0 },
      baseFire: { count: 1, speed: 380, lifetime: 2.2, gravityScale: 0.6, recoil: 20, trailMat: MAT.SPORE_DUST },
      light1: { primer: P.INFECTED }, light2: { primer: P.INFECTED },
      light3: { primer: P.INFECTED },
      heavy: { dmg: 1.9, primer: P.INFECTED, fire: { count: 4, spread: 0.35 } },
    },
  }));

  out.push(make('projectile', {
    id: 'bandolier_rig', cls: 'thrownRig', name: 'Scavenger\'s Bandolier',
    // weight: authored in weapons/bandolier_rig.pxm (issue #168 migration).
    reach: 240, cadenceMulBase: 2.0,
    windup: 31, active: 11, recovery: 60, cancelAfter: 0.5,
    damage: 20, poise: 1.0,
    primes: [P.BURNING, P.CORRODED, P.STAGGERED], detonates: [P.OILED],
    terrain: terr(4, 8, 0.6, { heat: 110, corrode: 0.4, displaces: true }),
    slots: slots(1, 3, 2), scaling: { finesse: 0.5, tech: 0.5 },
    tags: weaponTags('THROWN', 'AOE', 'DETONATOR', 'PRIMER_TOOL'),
    magazine: 12, reloadMs: 1050,
    desc: 'Every item on the belt has its own physics. Half the skill is knowing the arcs.',
    combo: {
      base: { kind: 'spawn', ox: 10, radius: 6, thickness: 6, halfAngle: 0 },
      baseFire: { count: 1, speed: 420, lifetime: 2.0, gravityScale: 0.9, recoil: 30 },
      light1: { primer: PrimerKind.BURNING },
      light2: { primer: PrimerKind.CORRODED },
      light3: { primer: PrimerKind.STAGGERED },
      heavy: { dmg: 2.0, fire: { count: 3, spread: 0.28 } },
    },
  }));

  return out;
}

/** Index by id. Built once by whoever owns the content registry. */
export function indexWeapons(defs: readonly WeaponDef[]): Map<string, WeaponDef> {
  const m = new Map<string, WeaponDef>();
  for (const d of defs) {
    if (m.has(d.id)) throw new Error(`duplicate weapon id: ${d.id}`);
    m.set(d.id, d);
  }
  return m;
}
